// SECTOR OVERVIEW — tap a sector's title on the home compass (Joel, 2026-10-18). One sheet with
// everything in that sector: where it stands (steps done, pace, open tasks), its Endeavour / Goal,
// what is due, every TKNE project with its steps and sub-steps, and the sector's tasks. The chat box
// sits at the TOP: the sector deity reads today's rundown aloud on open (useSectorDeity 'overview'
// mode) and answers there — with the same read/write it has in Video / Chat.
import React, { useMemo, useRef, useState } from 'react';
import { KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native';
import { Pause, Play, Send, X } from 'lucide-react-native';
import type { Sector } from '../domain/olmps';
import type { OracleProject } from '../olmps/useOracleProjects';
import { effectiveAims, type OracleSpine } from '../olmps/oracle-spine-data';
import { goalTextOf } from '../olmps/atlas-data';
import { useAuth } from '../auth/AuthProvider';
import { useAlignment } from '../olmps/useAlignment';
import { ALIGNMENT_STATUS } from '../olmps/alignment';
import { computeComplete, completeLabel, projectLeaves } from '../olmps/sectorProgress';
import { useKyrsTasks } from '../olmps/useKyrsTasks';
import { useCompassConfig } from '../olmps/sectorConfig';
import { sectorAudio as councilAudio } from '../council/councilAudio';
import { DeityFace, type SectorDeityCtl } from './SectorDeity';
import { SECTOR_COLOR, SECTOR_NAME, makeStyles, useV10 } from './theme';

const SECTOR_GLYPH: Record<Sector, string> = { A: '♄', E: '♃', H: '♂', S: '♀', P: '☿' };

/** yyyy-MM-dd (or ISO) → local midnight. */
function dayOf(v: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  if (!m) return null;
  return new Date(+m[1], +m[2] - 1, +m[3]);
}

function dueLabel(d: Date, today: Date): { text: string; late: boolean } {
  const days = Math.round((d.getTime() - today.getTime()) / 86400000);
  const date = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  if (days < 0) return { text: `${date} · ${-days}d overdue`, late: true };
  if (days === 0) return { text: `${date} · today`, late: false };
  if (days === 1) return { text: `${date} · tomorrow`, late: false };
  return { text: `${date} · in ${days}d`, late: false };
}

type Deadline = { key: string; title: string; project: string; date: Date };

function deadlinesOf(projects: OracleProject[]): Deadline[] {
  const out: Deadline[] = [];
  for (const p of projects) {
    const end = p.endDate ? dayOf(p.endDate) : null;
    if (end) out.push({ key: `${p.id}-end`, title: 'Project deadline', project: p.name, date: end });
    p.steps.forEach((s, i) => {
      const d = s.status !== 'C' && s.dueDate ? dayOf(s.dueDate) : null;
      if (d) out.push({ key: s.id, title: s.text.trim() || `Step ${i + 1}`, project: p.name, date: d });
      s.substeps.forEach((sub, j) => {
        const sd = !sub.checked && sub.dueDate ? dayOf(sub.dueDate) : null;
        if (sd) out.push({ key: sub.id, title: sub.text.trim() || `Sub-step ${j + 1}`, project: p.name, date: sd });
      });
    });
  }
  return out.sort((a, b) => a.date.getTime() - b.date.getTime());
}

/** DayEntry has no all-day flag: an entry starting at exactly local midnight whose end is its start (or a day+ later) has no time set. */
function hasNoTime(e: { start: Date; end: Date }): boolean {
  const s = e.start;
  if (s.getHours() !== 0 || s.getMinutes() !== 0 || s.getSeconds() !== 0 || s.getMilliseconds() !== 0) return false;
  const span = e.end.getTime() - s.getTime();
  return span === 0 || span >= 86400000;
}

const STATUS_WORD = { C: 'Done', S: 'Started', P: 'Pending' } as const;

export function SectorOverview({
  ctl,
  projects,
  projectsAreDemo,
  spine,
}: {
  ctl: SectorDeityCtl;
  projects: Record<Sector, OracleProject[]>;
  projectsAreDemo: boolean;
  spine: OracleSpine | null;
}) {
  const t = useV10();
  const styles = useStyles();
  const { width: W, height: H } = useWindowDimensions();
  const { mode, visible, sector, deity, lines, sending, briefing, send, close, audio, tasks, todayFor } = ctl;
  const [draft, setDraft] = useState('');
  const chatRef = useRef<ScrollView | null>(null);
  const open = mode === 'overview' && visible;
  const { session } = useAuth();
  const { board } = useAlignment(session?.user.id);
  const kyrsTasks = useKyrsTasks(session?.user.id);
  const { config } = useCompassConfig(session?.user.id);
  const seasonDays = config.game.alignmentWindows.endeavour;

  const list = useMemo(() => (projectsAreDemo ? [] : projects[sector] ?? []), [projects, projectsAreDemo, sector]);
  const data = useMemo(() => {
    const stepsAll = list.reduce((n, p) => n + p.steps.length, 0);
    const stepsDone = list.reduce((n, p) => n + p.steps.filter((x) => x.status === 'C').length, 0);
    const started = list.reduce((n, p) => n + p.steps.filter((x) => x.status === 'S').length, 0);
    const worst = list.reduce((m, p) => Math.max(m, ...p.drift.map((d) => d.score)), 0);
    const sectorTasks = tasks[sector] ?? [];
    return {
      stepsAll,
      stepsDone,
      started,
      pct: stepsAll ? Math.round((stepsDone / stepsAll) * 100) : 0,
      pace: worst >= 3 ? 'lapsed' : worst >= 1 ? 'behind' : 'onPace',
      openTasks: sectorTasks.filter((x) => !x.done),
      doneTasks: sectorTasks.filter((x) => x.done),
      deadlines: deadlinesOf(list),
      today: todayFor(sector).slice().sort((a, b) => a.start.getTime() - b.start.getTime()),
    } as const;
  }, [list, tasks, sector, todayFor]);

  // COMPLETE — the one shared calculation (Home / compass / this sheet always match).
  const comp = useMemo(
    () => computeComplete(sector, projectsAreDemo ? {} : projects, { now: new Date(), seasonDays, tasks: kyrsTasks }),
    [sector, projects, projectsAreDemo, seasonDays, kyrsTasks],
  );

  if (!open) return null;
  const color = SECTOR_COLOR[sector];
  const node = spine?.[sector] ?? null;
  const endeavour = (node?.endeavour ?? '').trim();
  const goal = node ? goalTextOf(node).trim() : '';
  const aims = node ? effectiveAims(node) : [];
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  // Alignment Score (Aim orb = the Home number): decimal, status word, Direction / Execution and ATLAS's reason.
  const al = board[sector].aim;
  // The pace chip reads the alignment score so it can never contradict it.
  const onPace = al.shown != null && al.shown >= 3;
  const paceText = onPace ? 'On pace' : ALIGNMENT_STATUS[al.status].word;
  const paceColor = onPace ? t.onPace : al.shown == null ? t.ivoryFaint : al.shown === 2 ? t.behind : t.lapsed;
  const headline =
    `${SECTOR_NAME[sector]}: ${completeLabel(comp.complete)}${comp.complete == null ? '' : ' complete'} · Alignment ${al.shown ?? '—'}` +
    ((al.shown == null || al.shown < 4) && al.reasonLine ? ` · ${al.reasonLine}` : '');
  const pctOf = (v: number | null) => (v == null ? '–' : `${Math.round(v * 100)}%`);
  const status = sending ? 'Thinking…' : briefing ? 'Preparing the rundown…' : audio.id && audio.loading ? 'Getting ready…' : audio.id ? 'Speaking' : '';
  const sheetW = Math.min(460, W - 20);
  const submit = () => {
    const v = draft;
    setDraft('');
    void send(v);
  };

  return (
    <View style={[StyleSheet.absoluteFill, { zIndex: 45 }]}>
      <Pressable style={[StyleSheet.absoluteFill, styles.scrim]} onPress={close} accessibilityLabel="Close sector overview" />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={[styles.sheet, { width: sheetW, left: (W - sheetW) / 2, top: Math.max(48, H * 0.07), bottom: Math.max(24, H * 0.04), borderColor: color }]}
      >
        <Pressable onPress={close} hitSlop={8} style={styles.x} accessibilityLabel="Close">
          <X size={16} color={t.ivory} />
        </Pressable>

        <Text style={[styles.headline, { color }]} numberOfLines={2}>
          {headline}
        </Text>

        <View style={styles.head}>
          <DeityFace id={deity.id} size={44} />
          <View style={{ flex: 1 }}>
            <Text style={[styles.sector, { color }]} numberOfLines={1}>
              {SECTOR_GLYPH[sector]} {SECTOR_NAME[sector].toUpperCase()}
            </Text>
            <Text style={styles.deity} numberOfLines={1}>
              {deity.name}
              {status ? <Text style={styles.status}>{`  ·  ${status}`}</Text> : null}
            </Text>
          </View>
          {audio.id ? (
            <Pressable onPress={() => void councilAudio.togglePause()} hitSlop={6} style={styles.pause} accessibilityLabel={audio.paused ? 'Resume' : 'Pause'}>
              {audio.paused ? <Play size={16} color={t.onGold} /> : <Pause size={16} color={t.goldBright} />}
            </Pressable>
          ) : null}
        </View>

        {/* Chat with the sector deity — at the top. */}
        <View style={styles.inputRow}>
          <TextInput
            value={draft}
            onChangeText={setDraft}
            onSubmitEditing={submit}
            returnKeyType="send"
            placeholder={`Ask ${deity.name} about ${SECTOR_NAME[sector]}…`}
            placeholderTextColor="#6b7280"
            style={styles.input}
          />
          <Pressable onPress={submit} style={styles.send} accessibilityLabel="Send">
            <Send size={16} color={t.onGold} />
          </Pressable>
        </View>
        <ScrollView
          ref={chatRef}
          style={styles.chat}
          contentContainerStyle={{ gap: 6, paddingVertical: 2 }}
          onContentSizeChange={() => lines.length > 1 && chatRef.current?.scrollToEnd({ animated: true })}
          showsVerticalScrollIndicator={false}
        >
          {lines.map((l, i) =>
            l.role === 'done' ? (
              <Text key={i} style={styles.done}>
                ✓ {l.text}
              </Text>
            ) : (
              <View key={i} style={[styles.bubble, l.role === 'user' ? styles.mine : styles.theirs]}>
                <Text style={[styles.bubbleText, l.role === 'user' && { color: t.onGold }]}>{l.text.replace(/\*\*|__/g, '')}</Text>
              </View>
            ),
          )}
          {sending || briefing ? (
            <View style={[styles.bubble, styles.theirs]}>
              <Text style={styles.bubbleText}>…</Text>
            </View>
          ) : null}
        </ScrollView>

        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 14, paddingBottom: 6 }} showsVerticalScrollIndicator={false}>
          {/* Standing */}
          <View style={styles.card}>
            <View style={styles.standRow}>
              <Text style={[styles.pct, { color }]}>{data.pct}%</Text>
              <View style={{ flex: 1, gap: 6 }}>
                <View style={styles.bar}>
                  <View style={[styles.barFill, { width: `${data.pct}%`, backgroundColor: color }]} />
                </View>
                <Text style={styles.muted}>
                  {data.stepsDone} of {data.stepsAll} project steps done{data.started ? ` · ${data.started} started` : ''}
                </Text>
              </View>
            </View>
            <View style={styles.alignRow}>
              <Text style={[styles.alignScore, { color }]}>{al.raw == null ? '–' : al.raw.toFixed(1)}</Text>
              <View style={{ flex: 1 }}>
                <Text style={styles.rowText}>
                  {ALIGNMENT_STATUS[al.status].word} · Direction {pctOf(al.direction)} · Execution {pctOf(al.execution)}
                </Text>
                <Text style={styles.muted}>{al.reason}</Text>
              </View>
            </View>
            <View style={styles.chips}>
              <Chip label={paceText} color={paceColor} />
              <Chip label={`${list.length} ${list.length === 1 ? 'project' : 'projects'}`} />
              <Chip label={`${data.openTasks.length} open ${data.openTasks.length === 1 ? 'task' : 'tasks'}`} />
              {data.doneTasks.length ? <Chip label={`${data.doneTasks.length} done (14d)`} /> : null}
              {data.deadlines.some((d) => d.date < now) ? <Chip label={`${data.deadlines.filter((d) => d.date < now).length} overdue`} color={t.lapsed} /> : null}
            </View>
          </View>

          {/* Endeavour / Goal / Aims */}
          <Section title="Direction">
            <Field label="Endeavour" value={endeavour || 'Not set yet'} empty={!endeavour} />
            <Field label="Goal" value={goal || 'Not set yet'} empty={!goal} />
            {aims.length ? <Field label={aims.length === 1 ? 'Aim' : 'Aims'} value={aims.map((a) => a.label).join(' · ')} /> : null}
          </Section>

          {/* Deadlines */}
          <Section title="Deadlines">
            {data.deadlines.length ? (
              data.deadlines.slice(0, 8).map((d) => {
                const due = dueLabel(d.date, now);
                return (
                  <View key={d.key} style={styles.row}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.rowText} numberOfLines={1}>
                        {d.title}
                      </Text>
                      <Text style={styles.muted} numberOfLines={1}>
                        {d.project}
                      </Text>
                    </View>
                    <Text style={[styles.due, due.late && { color: t.lapsed }]}>{due.text}</Text>
                  </View>
                );
              })
            ) : (
              <Text style={styles.empty}>No dates set on any {SECTOR_NAME[sector]} step yet.</Text>
            )}
          </Section>

          {/* Projects */}
          <Section title={`Projects (${list.length})`}>
            {list.length ? (
              list.map((p) => {
                const done = p.steps.filter((x) => x.status === 'C').length;
                const next = p.steps.find((x) => x.status !== 'C' && x.text.trim());
                return (
                  <View key={p.id} style={styles.project}>
                    <View style={styles.row}>
                      <Text style={styles.projName} numberOfLines={1}>
                        {p.name}
                      </Text>
                      {comp.empty.some((x) => x.id === p.id) ? <Text style={styles.noTasks}>no tasks yet</Text> : null}
                      <Text style={styles.muted}>
                        {done}/{p.steps.length}
                      </Text>
                    </View>
                    <View style={styles.bar}>
                      <View style={[styles.barFill, { width: `${p.steps.length ? (done / p.steps.length) * 100 : 0}%`, backgroundColor: color }]} />
                    </View>
                    {next ? <Text style={styles.next}>Next: {next.text.trim()}</Text> : null}
                    {p.steps.map((s, i) => (
                      <View key={s.id} style={{ gap: 2 }}>
                        <View style={styles.stepRow}>
                          <View style={[styles.dot, { borderColor: color }, s.status === 'C' && { backgroundColor: color }, s.status === 'S' && { backgroundColor: `${color}66` }]} />
                          <Text style={[styles.stepText, s.status === 'C' && styles.struck, !s.text.trim() && styles.emptyStep]} numberOfLines={2}>
                            {s.text.trim() || `Step ${i + 1} (untitled)`}
                          </Text>
                          <Text style={styles.stepStatus}>{STATUS_WORD[s.status]}</Text>
                        </View>
                        {s.substeps.map((sub, j) => (
                          <View key={sub.id} style={[styles.stepRow, { paddingLeft: 20 }]}>
                            <Text style={[styles.subTick, sub.checked && { color }]}>{sub.checked ? '✓' : '○'}</Text>
                            <Text style={[styles.subText, sub.checked && styles.struck]} numberOfLines={1}>
                              {sub.text.trim() || `Sub-step ${j + 1}`}
                            </Text>
                          </View>
                        ))}
                      </View>
                    ))}
                  </View>
                );
              })
            ) : (
              <Text style={styles.empty}>No projects in {SECTOR_NAME[sector]} yet. Set a project's sector (or its group's) in TKNE.</Text>
            )}
          </Section>

          {/* Finished this season */}
          <Section title={`Finished this season (${comp.finished.length})`}>
            {comp.finished.length ? (
              comp.finished.map((p) => (
                <View key={p.id} style={styles.row}>
                  <Text style={[styles.rowText, { flex: 1 }]} numberOfLines={1}>
                    {p.name}
                  </Text>
                  <Text style={styles.muted}>{projectLeaves(p).length} tasks</Text>
                </View>
              ))
            ) : (
              <Text style={styles.empty}>Nothing finished this season yet.</Text>
            )}
          </Section>

          {/* Today + tasks */}
          {data.today.length ? (
            <Section title="Today">
              {data.today.map((e) => (
                <View key={e.id} style={styles.row}>
                  <Text style={[styles.rowText, { flex: 1 }, e.completed && styles.struck]} numberOfLines={1}>
                    {e.title}
                  </Text>
                  <Text style={styles.due}>{hasNoTime(e) ? '' : e.start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</Text>
                </View>
              ))}
            </Section>
          ) : null}
          <Section title="Tasks">
            {data.openTasks.length || data.doneTasks.length ? (
              <>
                {data.openTasks.slice(0, 10).map((x) => (
                  <View key={x.id} style={styles.stepRow}>
                    <Text style={styles.subTick}>○</Text>
                    <Text style={styles.stepText} numberOfLines={1}>
                      {x.title}
                    </Text>
                  </View>
                ))}
                {data.openTasks.length > 10 ? <Text style={styles.muted}>+ {data.openTasks.length - 10} more open</Text> : null}
                {data.doneTasks.slice(0, 4).map((x) => (
                  <View key={x.id} style={styles.stepRow}>
                    <Text style={[styles.subTick, { color }]}>✓</Text>
                    <Text style={[styles.stepText, styles.struck]} numberOfLines={1}>
                      {x.title}
                    </Text>
                  </View>
                ))}
              </>
            ) : (
              <Text style={styles.empty}>No tasks tagged {SECTOR_NAME[sector]}.</Text>
            )}
          </Section>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );

  function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>{title}</Text>
        {children}
      </View>
    );
  }
  function Field({ label, value, empty }: { label: string; value: string; empty?: boolean }) {
    return (
      <View style={{ gap: 1 }}>
        <Text style={styles.fieldLabel}>{label}</Text>
        <Text style={[styles.rowText, empty && styles.emptyStep]}>{value}</Text>
      </View>
    );
  }
  function Chip({ label, color: c }: { label: string; color?: string }) {
    return (
      <View style={[styles.chip, c ? { borderColor: c } : null]}>
        <Text style={[styles.chipText, c ? { color: c } : null]}>{label}</Text>
      </View>
    );
  }
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    scrim: { backgroundColor: 'rgba(4,7,18,0.55)' },
    sheet: { position: 'absolute', padding: 14, paddingTop: 14, gap: 10, borderRadius: 22, borderWidth: 1.5, backgroundColor: 'rgba(9,14,32,0.97)', boxShadow: '0 12px 36px rgba(0,0,0,0.55)' } as object,
    x: { position: 'absolute', top: 10, right: 10, zIndex: 2, width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: 'rgba(214,180,106,0.5)', backgroundColor: 'rgba(9,14,32,0.9)' },
    headline: { fontSize: 12.5, fontWeight: '800', lineHeight: 17, paddingRight: 40 },
    noTasks: { color: t.ivoryFaint, fontSize: 11, fontStyle: 'italic' },
    head: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingRight: 40 },
    sector: { fontSize: 18, fontWeight: '800', letterSpacing: 2.4 },
    deity: { fontSize: 13, fontWeight: '700', color: t.ivory, letterSpacing: 0.6 },
    status: { color: t.goldBright, fontWeight: '600' },
    pause: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', borderWidth: 1.5, borderColor: t.gold, backgroundColor: 'rgba(9,14,32,0.9)' },
    inputRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    input: { flex: 1, height: 42, borderRadius: 21, paddingHorizontal: 14, backgroundColor: '#ffffff', borderWidth: 1.5, borderColor: t.gold, color: '#111827', fontSize: 14 },
    send: { width: 42, height: 42, borderRadius: 21, alignItems: 'center', justifyContent: 'center', backgroundColor: t.gold },
    chat: { maxHeight: 170, flexGrow: 0 },
    bubble: { maxWidth: '94%', borderRadius: 14, paddingHorizontal: 11, paddingVertical: 7, borderWidth: 1 },
    theirs: { alignSelf: 'flex-start', backgroundColor: 'rgba(16,24,48,0.92)', borderColor: 'rgba(214,180,106,0.35)' },
    mine: { alignSelf: 'flex-end', backgroundColor: t.gold, borderColor: t.gold },
    bubbleText: { color: '#f3ead2', fontSize: 14, lineHeight: 19 },
    done: { color: t.goldBright, fontSize: 12, fontStyle: 'italic', paddingHorizontal: 4 },
    card: { gap: 8, padding: 12, borderRadius: 16, backgroundColor: t.navyRaised },
    standRow: { flexDirection: 'row', alignItems: 'center', gap: 14 },
    pct: { fontSize: 34, fontWeight: '800', minWidth: 78 },
    bar: { height: 7, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.08)', overflow: 'hidden' },
    barFill: { height: '100%', borderRadius: 4 },
    alignRow: { flexDirection: 'row', alignItems: 'center', gap: 14 },
    alignScore: { fontSize: 26, fontWeight: '800', minWidth: 78 },
    chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    chip: { paddingHorizontal: 9, paddingVertical: 4, borderRadius: 999, borderWidth: 1, borderColor: t.navyLine },
    chipText: { fontSize: 11.5, fontWeight: '700', color: t.ivorySoft },
    sectionTitle: { fontSize: 11, fontWeight: '800', letterSpacing: 1.6, color: t.goldBright, textTransform: 'uppercase' },
    fieldLabel: { fontSize: 10.5, fontWeight: '700', letterSpacing: 1, color: t.ivorySoft, textTransform: 'uppercase' },
    row: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    rowText: { color: t.ivory, fontSize: 14 },
    muted: { color: t.ivorySoft, fontSize: 12 },
    due: { color: t.goldBright, fontSize: 12, fontWeight: '700' },
    empty: { color: t.ivorySoft, fontSize: 13, fontStyle: 'italic' },
    project: { gap: 6, paddingTop: 4 },
    projName: { flex: 1, color: t.ivory, fontSize: 15, fontWeight: '800', letterSpacing: 0.4 },
    next: { color: t.goldBright, fontSize: 12.5 },
    stepRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    dot: { width: 10, height: 10, borderRadius: 5, borderWidth: 1.5 },
    stepText: { flex: 1, color: t.ivory, fontSize: 13 },
    stepStatus: { color: t.ivorySoft, fontSize: 11 },
    struck: { textDecorationLine: 'line-through', opacity: 0.6 },
    emptyStep: { color: t.ivorySoft, fontStyle: 'italic' },
    subTick: { width: 12, color: t.ivorySoft, fontSize: 12, textAlign: 'center' },
    subText: { flex: 1, color: t.ivorySoft, fontSize: 12.5 },
  }),
);
